import * as pdfjsLib from "pdfjs-dist";

// Ensure the PDF worker is correctly set
pdfjsLib.GlobalWorkerOptions.workerSrc = `/js/pdf.worker.min.mjs`;

export async function checkFileType(url) {
  try {
    const response = await fetch(url);
    const contentType = response.headers.get("content-type");

    if (contentType && contentType.includes("image")) {
      return "image";
    } else if (contentType && contentType.includes("video")) {
      return "video";
    } else {
      // If content type is neither image nor video, you can handle it accordingly
      console.log("Unsupported content type:", contentType);
      return "unsupported";
    }
  } catch (error) {
    console.error("Error checking file type:", error);
    return "error";
  }
}

export const isCompanyLogin = () => {
  const companyLogin = localStorage.getItem("user_id");
  return companyLogin ? true : false;
};

/**
 * Generates a thumbnail for the first page of a PDF.
 * @param {File|Blob} pdfFile - The PDF file to generate a thumbnail for.
 * @returns {Promise<string>} - A promise that resolves to the base64 image data URL.
 */
export const generatePdfThumbnail = async (pdfFile, intl) => {
  try {
    let pdfBlob;

    // Check if the input is a File object
    if (pdfFile instanceof File) {
      pdfBlob = pdfFile; // Use the File directly
    } else {
      // Otherwise, assume it's a URL and fetch the PDF
      const response = await fetch(pdfFile);
      if (!response.ok) {
        throw new Error(
          intl.formatMessage({ id: "failed-to-fetch-the-pdf-file" })
        );
      }
      pdfBlob = await response.blob(); // Get the PDF file as a Blob
    }

    const fileReader = new FileReader();

    return new Promise((resolve, reject) => {
      fileReader.onload = async function () {
        try {
          const typedArray = new Uint8Array(this.result);
          const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise;
          const firstPage = await pdf.getPage(1);

          const viewport = firstPage.getViewport({ scale: 1 });
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          canvas.height = viewport.height;
          canvas.width = viewport.width;

          await firstPage.render({
            canvasContext: context,
            viewport,
          }).promise;

          const imageDataUrl = canvas.toDataURL();
          resolve(imageDataUrl); // Resolve with the generated thumbnail
        } catch (error) {
          console.error(
            `${intl.formatMessage({ id: "error-generating-pdf-thumbnail" })}:`,
            error
          );
          reject(error);
        }
      };

      fileReader.readAsArrayBuffer(pdfBlob);
    });
  } catch (error) {
    console.error(
      `${intl.formatMessage({
        id: "error-in-thumbnail-generation-function",
      })}:`,
      error
    );
    throw new Error(
      intl.formatMessage({ id: "failed-to-generate-pdf-thumbnail" })
    );
  }
};

export const calculateExpirationTime = (unit, duration) => {
  const SECONDS_IN_DAY = 86400;
  switch (unit?.toLowerCase()) {
    case "days":
      return duration * SECONDS_IN_DAY;
    case "weeks":
      return duration * 7 * SECONDS_IN_DAY;
    case "month":
      return duration * 30 * SECONDS_IN_DAY; // Approximating month to 30 days
    default:
      return SECONDS_IN_DAY; // Default 1 day if unit not specified
  }
};

export const formatCookieExpirationTime = (expirationTime) => {
  if (!expirationTime || expirationTime <= 0) {
    return null;
  }

  // Calculate different units
  const minutes = Math.floor(expirationTime / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);

  // Return the most appropriate unit
  if (months > 0) {
    return {
      value: months,
      unit: months === 1 ? "month" : "months",
    };
  } else if (weeks > 0) {
    return {
      value: weeks,
      unit: weeks === 1 ? "week" : "weeks",
    };
  } else if (days > 0) {
    return {
      value: days,
      unit: days === 1 ? "day" : "days",
    };
  } else if (hours > 0) {
    return {
      value: hours,
      unit: hours === 1 ? "hour" : "hours",
    };
  } else {
    return {
      value: Math.max(minutes, 1), // Show at least 1 minute
      unit: minutes === 1 ? "minute" : "minutes",
    };
  }
};

/**
 * Browsers only allow window.close() on tabs opened via script.
 * Try to close; if the tab is still open, redirect to a confirmation page.
 *
 * For partial-exit redirects, also persist a localStorage flag so that
 * a refresh of the thank-you page (which loses location.state) is still
 * recognised as a partial exit and does not wipe the resume tmp_response_id.
 * The flag is cleared by the eventual final-submission flow, which calls
 * localStorage.clear() on a non-partial-exit ThankYou render.
 */
export const attemptCloseTabOrRedirect = (history, redirectState) => {
  if (redirectState?.partialExit) {
    try {
      localStorage.setItem("survey_partial_exit", "1");
    } catch (e) {
      // localStorage may be unavailable (private mode, etc.); fall back to state-only
    }
  }
  window.close();
  setTimeout(() => {
    history.push({
      pathname: "/surveyThankYou",
      state: redirectState,
    });
  }, 300);
};
